
import { MEDIA_TYPE } from '../base/media';
import { getParticipantById } from '../base/participants';
import { isSharedVideoParticipant } from '../base/participants/functions';
import { StateListenerRegistry } from '../base/redux';
import { getTrackByMediaTypeAndParticipant } from '../base/tracks';
import { isParticipantIdAllowedInFocusMode } from '../focus-mode/functions';
import VideoLayout from '../video-layout/VideoLayout';

import { selectParticipantInLargeVideo } from './actions.any';


/**
 * The last selection made by the stage-update selector below, reused while nothing in it changed.
 *
 * @type {Object|undefined}
 */
let _lastStageSelection;

/**
 * Updates the on stage participant video.
 *
 * The selector used to return a new object literal on every call. StateListenerRegistry compares selections by
 * reference, so the listener - a FORCED large-video update that re-runs setStream, the avatar, the container
 * show/hide and their DOM work - ran on every single redux action dispatched anywhere in the app. It now
 * changes only when the stage participant, their participant record or the video/screenshare track that can be
 * on stage changes, which is everything the update depends on.
 */
StateListenerRegistry.register(
    /* selector */ state => {
        const { participantId } = state['features/large-video'];
        const participant = participantId ? getParticipantById(state, participantId) : undefined;
        const tracks = state['features/base/tracks'];
        const videoTrack = participantId
            ? getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, participantId)
            : undefined;
        const screenshareTrack = participantId
            ? getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, participantId)
            : undefined;

        if (!_lastStageSelection
                || _lastStageSelection.participantId !== participantId
                || _lastStageSelection.participant !== participant
                || _lastStageSelection.videoTrack !== videoTrack
                || _lastStageSelection.screenshareTrack !== screenshareTrack) {
            _lastStageSelection = {
                participant,
                participantId,
                screenshareTrack,
                videoTrack
            };
        }

        return _lastStageSelection;
    },
    /* listener */ ({ participant, participantId }, store) => {
        if (!participantId) {
            return;
        }

        // Focus Mode may have just hidden whoever is on the stage - a guest who
        // was pinned, or a host who was demoted. Re-elect instead of rendering
        // them.
        if (!isParticipantIdAllowedInFocusMode(store.getState(), participantId)) {
            store.dispatch(selectParticipantInLargeVideo(undefined, {
                selectionSource: 'focus-mode',
                forceSelection: true
            }));

            return;
        }

        // Skip updateLargeVideo for fake participants (shared video like YouTube)
        // Shared video is handled by the SharedVideo component, not VideoLayout
        if (participant && isSharedVideoParticipant(participant)) {
            return;
        }

        VideoLayout.updateLargeVideo(participantId, true);
    }
);

